import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FileUploadService } from '@core/services/file-upload.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { KENDO_ICONS } from '@progress/kendo-angular-icons';
import {
  KENDO_UPLOADS,
  RemoveEvent,
  UploadEvent,
} from '@progress/kendo-angular-upload';
import { IconComponent } from '@shared/components/icon/icon.component';
import { Icon } from '@shared/components/icon/icon.enum';
import { ValidatorType } from '@shared/models/enums/validator-type.enum';
import { FileUpload } from '@shared/models/question-types/file-upload';
import { ValidatorComponent } from '../../validator/validator.component';
import { QuestionBaseComponent } from '../question-base/question-base.component';
@Component({
  selector: 'app-file-upload',
  standalone: true,
  imports: [
    CommonModule,
    ...KENDO_UPLOADS,
    ValidatorComponent,
    QuestionBaseComponent,
    TranslateModule,
    ...KENDO_ICONS,
    IconComponent,
  ],
  templateUrl: './file-upload.component.html',
  styleUrl: './file-upload.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class FileUploadComponent implements OnInit {
  @Input({ required: true }) question!: FileUpload;
  @Input({ required: true }) control!: any;
  @Input() isFirstQuestion: boolean = false;

  @ViewChild('removeButton', { read: ElementRef })
  removeButton?: ElementRef<HTMLButtonElement>;
  Icon = Icon;
  ValidatorType = ValidatorType;
  file!: File | undefined;
  uploadStatus: 'none' | 'uploading' | 'success' | 'error' = 'none';
  errorMessage: string = '';
  uploadProgress: number = 0;

  private questId?: string;
  private responseId?: string;

  private static readonly FILE_SIZE_UNITS = ['Bytes', 'KB', 'MB', 'GB'];
  private static readonly BYTES_PER_UNIT = 1024;

  constructor(
    private fileUploadService: FileUploadService,
    private translateService: TranslateService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      this.questId = params.get('questId') || undefined;
      this.responseId = params.get('responseId') || undefined;
    });
  }

  private updateControlValue(value: string | null): void {
    this.control.setValue(value);
    this.control.markAsDirty();
    this.control.updateValueAndValidity();
  }

  private resetState(): void {
    this.file = undefined;
    this.uploadStatus = 'none';
    this.errorMessage = '';
    this.uploadProgress = 0;
    this.updateControlValue(null);
  }

  private performUpload(file: File): void {
    this.uploadStatus = 'uploading';
    this.errorMessage = '';
    this.uploadProgress = 0;

    this.fileUploadService.mediaUpload(file, this.question).subscribe({
      next: (progressEvent) => {
        if (
          progressEvent.type === 'progress' &&
          progressEvent.progress !== undefined
        ) {
          this.uploadProgress = progressEvent.progress;
        } else if (progressEvent.type === 'complete') {
          this.uploadStatus = 'success';
          this.uploadProgress = 100;
          this.updateControlValue(file.name);
          // Move focus to remove button after successful upload
          this.focusRemoveButton();
        }
      },
      error: (error: any) => {
        this.uploadStatus = 'error';
        this.uploadProgress = 0;
        this.errorMessage = this.getUserFriendlyErrorMessage(error);
        this.updateControlValue(null);
      },
    });
  }

  private focusRemoveButton(): void {
    // Use setTimeout to ensure DOM is updated
    setTimeout(() => {
      if (this.removeButton?.nativeElement) {
        this.removeButton.nativeElement.focus();
      }
    }, 100);
  }

  uploadEventHandler(e: UploadEvent) {
    const file = e.files[0].rawFile;
    if (file) {
      this.file = file;
      this.performUpload(file);
    }
    e.preventDefault();
  }

  removeEventHandler(e: RemoveEvent) {
    this.performRemove();
    e.preventDefault();
  }

  private performRemove(): void {
    this.fileUploadService
      .mediaRemove(this.question, this.questId ?? '', this.responseId ?? '')
      .subscribe({
        next: () => {
          this.resetState();
        },
        error: (error: any) => {
          this.errorMessage = this.getUserFriendlyErrorMessage(error);
          this.resetState();
        },
      });
  }

  removeFile() {
    this.performRemove();
  }

  retryUpload() {
    if (this.file) {
      this.performUpload(this.file);
    }
  }

  private getUserFriendlyErrorMessage(error: any): string {
    const statusErrorMap: Record<number, string> = {
      400: 'fileupload.error.badrequest',
      401: 'fileupload.error.unauthorized',
      403: 'fileupload.error.forbidden',
      404: 'fileupload.error.notfound',
      413: 'fileupload.error.filetoolarge',
      415: 'fileupload.error.unsupportedfiletype',
      500: 'fileupload.error.servererror',
      503: 'fileupload.error.serviceunavailable',
    };

    if (error.status && statusErrorMap[error.status]) {
      return this.translateService.instant(statusErrorMap[error.status]);
    }

    if (error.message) {
      const message = error.message.toLowerCase();
      const messagePatterns: Array<{ patterns: string[]; key: string }> = [
        {
          patterns: ['network', 'connection'],
          key: 'fileupload.error.networkerror',
        },
        { patterns: ['timeout'], key: 'fileupload.error.timeout' },
        {
          patterns: ['file size', 'too large'],
          key: 'fileupload.error.filetoolarge',
        },
      ];

      for (const { patterns, key } of messagePatterns) {
        if (patterns.some((pattern) => message.includes(pattern))) {
          return this.translateService.instant(key);
        }
      }
    }

    return this.translateService.instant('fileupload.error.uploadfailed');
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const unitIndex = Math.floor(
      Math.log(bytes) / Math.log(FileUploadComponent.BYTES_PER_UNIT),
    );
    const size = parseFloat(
      (bytes / Math.pow(FileUploadComponent.BYTES_PER_UNIT, unitIndex)).toFixed(
        2,
      ),
    );

    return `${size} ${FileUploadComponent.FILE_SIZE_UNITS[unitIndex]}`;
  }

  get fileStatusMessage(): string {
    const fileName = this.control.value || this.file?.name;
    if (!fileName) return '';

    if (this.uploadStatus === 'uploading') {
      return this.translateService.instant('fileupload.uploading', {
        fileName,
        progress: this.uploadProgress,
      });
    }

    if (this.uploadStatus === 'success') {
      return this.translateService.instant('fileupload.uploadcomplete', {
        fileName,
      });
    }

    if (this.uploadStatus === 'error') {
      return this.translateService.instant('fileupload.uploadfailed', {
        fileName,
      });
    }

    return this.translateService.instant('fileupload.fileselected', {
      fileName,
    });
  }
}
